import { Card } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import numberHumanizer from '@/helpers/numberHumanizer';
import { buildBacklinkUrl } from '../utils/backlinkRoutes';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import './BacklinkTabStats.css';

const TAB_ITEMS = [
  ['lost', 'حذف لینک', 'danger'],
  ['unavailable', 'عدم دسترسی', 'warning'],
  ['active', 'فعال', 'success'],
  ['broken', 'شکسته', 'danger'],
  ['nofollow', 'نوفالو', 'secondary'],
  ['follow', 'فالو', 'success'],
  ['new', 'جدید', 'primary'],
  ['high_spam', 'دامنه اسپم', 'danger'],
  ['backlink_spam', 'لینک اسپم', 'warning'],
  ['redirected', 'ریدایرکت‌ها', 'info'],
  ['in_text', 'در متن', 'primary'],
  ['best', 'بهترین', 'success'],
  ['home', 'از خانه', 'info'],
  ['noindex', 'نولیندکس', 'secondary'],
  ['canonical', 'کنونیکال', 'secondary']
];

const BacklinkTabStats = ({ counts, loading = false }: Record<string, any>) => {
  const [searchParams] = useSearchParams();
  const { domain_slug: domainSlug, domain: domainName } = useParams();
  const total = counts?.all ?? 0;

  const buildTabUrl = (key: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('tab', key);

    return buildBacklinkUrl(
      '/backlink/backlinks',
      domainSlug,
      domainName,
      params
    );
  };

  return (
    <Card className="h-100 w-100 backlink-tab-stats">
      <FalconCardHeader
        title="آمار تب‌های بک‌لینک"
        titleTag="h6"
        className="py-2"
        light
        endEl={
          <HelpTooltip html="درصد هر تب نسبت به کل بک‌لینک‌هاست؛ یک لینک می‌تواند در چند تب هم‌زمان باشد، پس جمع درصدها لزوماً ۱۰۰ نیست." />
        }
      />
      <Card.Body className="backlink-tab-stats__body pt-2 pb-3">
        <div className="backlink-tab-stats__grid">
          {TAB_ITEMS.map(([key, label, tone]) => {
            const count = counts?.[key] ?? 0;
            const percentage = total ? Math.round((count * 100) / total) : 0;
            const barWidth = Math.min(Math.max(percentage, 0), 100);

            return (
              <Link
                key={key}
                to={buildTabUrl(key)}
                className={classNames(
                  'backlink-tab-stats__item',
                  `backlink-tab-stats__item--${tone}`
                )}
                aria-label={`نمایش تب ${label}`}
              >
                <div className="backlink-tab-stats__row">
                  <div className="backlink-tab-stats__meta">
                    <span className="backlink-tab-stats__label">{label}</span>
                    <span className="backlink-tab-stats__count">
                      {loading ? (
                        <span className="invisible" aria-hidden>
                          (0)
                        </span>
                      ) : (
                        `(${numberHumanizer(count)})`
                      )}
                    </span>
                  </div>
                  <span className="backlink-tab-stats__pct">
                    {loading ? (
                      <span className="invisible" aria-hidden>
                        ۰٪
                      </span>
                    ) : (
                      `${percentage}٪`
                    )}
                  </span>
                </div>
                <div
                  className="backlink-tab-stats__track"
                  role="progressbar"
                  aria-label={`درصد ${label}`}
                  aria-valuenow={percentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="backlink-tab-stats__bar"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </Link>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

BacklinkTabStats.propTypes = {
  counts: PropTypes.object,
  loading: PropTypes.bool
};

export default BacklinkTabStats;
